// Quick reply button component with keyboard shortcuts - supports multi-select
// @requirement CLAUDE.md: Quick-reply buttons with keyboard shortcuts (1-4)

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface QuickReplyButtonsProps {
  options: string[]
  onSelect: (option: string | string[]) => void
  disabled?: boolean
  enableKeyboardShortcuts?: boolean
  multiSelect?: boolean
  className?: string
}

export function QuickReplyButtons({
  options,
  onSelect,
  disabled = false,
  enableKeyboardShortcuts = true,
  multiSelect = false,
  className,
}: QuickReplyButtonsProps): JSX.Element {
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set())

  // Reset selections when options change
  useEffect(() => {
    setSelectedOptions(new Set())
  }, [options])

  useEffect(() => {
    if (!enableKeyboardShortcuts || disabled || options.length === 0) return

    const handleKeyPress = (e: KeyboardEvent) => {
      const key = e.key
      const numKey = parseInt(key, 10)

      // Number keys (1-4) toggle selection in multi-select or select in single-select
      if (numKey >= 1 && numKey <= options.length && numKey <= 4) {
        e.preventDefault()
        const option = options[numKey - 1]
        if (multiSelect) {
          handleToggle(option)
        } else {
          onSelect(option)
        }
      }

      // Enter key to submit in multi-select mode
      if (multiSelect && e.key === 'Enter' && selectedOptions.size > 0) {
        e.preventDefault()
        handleSubmit()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [options, onSelect, disabled, enableKeyboardShortcuts, multiSelect, selectedOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (option: string): void => {
    console.log('[QuickReplyButtons] Toggle clicked:', { option, multiSelect })
    setSelectedOptions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(option)) {
        newSet.delete(option)
      } else {
        newSet.add(option)
      }
      console.log('[QuickReplyButtons] New selection:', Array.from(newSet))
      return newSet
    })
  }

  const handleSubmit = (): void => {
    if (selectedOptions.size > 0) {
      onSelect(Array.from(selectedOptions))
      setSelectedOptions(new Set())
    }
  }

  if (options.length === 0) return <></>

  return (
    <div className={cn('space-y-2 p-2', className)} role="group" aria-label="Quick reply options">
      <div className="flex flex-wrap gap-2">
        {options.map((option, index) => {
          const shortcutKey = index + 1
          const showShortcut = enableKeyboardShortcuts && shortcutKey <= 4
          const isSelected = selectedOptions.has(option)

          return (
            <Button
              key={option}
              variant="outline"
              size="sm"
              onClick={() => {
                console.log('[QuickReplyButtons] Button clicked:', { option, multiSelect, disabled })
                if (multiSelect) {
                  handleToggle(option)
                } else {
                  onSelect(option)
                }
              }}
              disabled={disabled}
              className={cn(
                'relative border-kaycoo-teal-dark text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale',
                isSelected && multiSelect && 'bg-kaycoo-teal text-white hover:bg-kaycoo-teal-dark',
              )}
              aria-label={showShortcut ? `${option} (Press ${shortcutKey})` : option}
              aria-pressed={multiSelect ? isSelected : undefined}
            >
              {showShortcut && (
                <span className={cn(
                  'mr-2 flex h-5 w-5 items-center justify-center rounded text-xs font-bold',
                  isSelected && multiSelect ? 'bg-white text-kaycoo-teal-dark' : 'bg-kaycoo-teal-dark text-white',
                )}>
                  {shortcutKey}
                </span>
              )}
              {isSelected && multiSelect && <Check className="mr-1 h-3 w-3" />}
              {option}
            </Button>
          )
        })}
      </div>
      {multiSelect && selectedOptions.size > 0 && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-kaycoo-teal-light bg-kaycoo-teal-pale p-2">
          <span className="text-sm text-kaycoo-charcoal">
            {selectedOptions.size} selected
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedOptions(new Set())}
              disabled={disabled}
              className="text-kaycoo-charcoal-2 hover:bg-kaycoo-white-2"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={disabled}
              className="bg-kaycoo-teal text-white hover:bg-kaycoo-teal-dark"
            >
              Submit ({selectedOptions.size})
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
